import { Button, Checkbox, CircularProgress, Typography } from '@mui/material';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'src/redux/store';
import { formatDateTime, formatNumber } from 'src/app/modules/OpmPd/utils/commonHelper';
import AppTable from 'src/app/common/components/AppTable/AppTable';
import { useTranslation } from 'src/app/common/hook/translation.hook';
import { exportAdminReportBatchFileFinalPolicies } from 'src/app/modules/OpmPd/network/opm-pd-admin-report';
import { useAdminReportBatchFilePruTable } from './useAdminReportBatchFilePruTable';
import { exportAdminReportBatchFileSubmittedPolicies } from 'src/app/modules/OpmPd/network/opm-pd-admin-report';
import PruFilter, { PruFilterItemType } from 'src/app/common/components/PruTableTab/PruFilter';
import _isEqual from 'lodash/isEqual';
import { AlertType, appendAlertItem, AuthenticationState } from '@pruforce/common-adminweb-sdk';

const initialFilters = {};
const SELECTED_BATCH_IDS_LIMIT = 20;
type PolicyExportProps = {
  batchIds: string[];
  onExportSuccess?: () => void;
};

const PolicyExport = (props: PolicyExportProps) => {
  const { batchIds, onExportSuccess } = props;
  const [isExportingSubmittedPolicies, setIsExportingSubmittedPolicies] = React.useState(false);
  const [isExportingFinalPolicies, setIsExportingFinalPolicies] = React.useState(false);

  const t = useTranslation();
  const dispatch = useDispatch();

  const { user } = useSelector<RootState, AuthenticationState>((state) => state.auth);

  const handlePolicyExport = (module: 'submittedPolicies' | 'finalPolicies') => async () => {
    try {
      if (module === 'submittedPolicies') {
        setIsExportingSubmittedPolicies(true);
        await exportAdminReportBatchFileSubmittedPolicies({ batchIds }, dispatch);
      } else {
        setIsExportingFinalPolicies(true);
        await exportAdminReportBatchFileFinalPolicies({ batchIds }, dispatch);
      }

      dispatch(
        appendAlertItem([
          {
            severity: AlertType.SUCCESS,
            title: 'Success',
            content: `Yêu cầu xuất báo cáo đã được ghi nhận. File báo cáo sẽ được gửi đến Email ${user?.sub}`,
          },
        ]),
      );
      onExportSuccess?.();
    } catch (_) {}
    setIsExportingSubmittedPolicies(false);
    setIsExportingFinalPolicies(false);
  };

  return (
    <>
      <Button
        variant="contained"
        onClick={handlePolicyExport('submittedPolicies')}
        disabled={isExportingFinalPolicies || isExportingSubmittedPolicies || batchIds.length === 0}
        startIcon={isExportingSubmittedPolicies ? <CircularProgress size={16} color="inherit" /> : undefined}
      >
        {t('opm_pd.submittedExport')}
      </Button>
      <Button
        variant="contained"
        onClick={handlePolicyExport('finalPolicies')}
        disabled={isExportingFinalPolicies || isExportingSubmittedPolicies || batchIds.length === 0}
        startIcon={isExportingFinalPolicies ? <CircularProgress size={16} color="inherit" /> : undefined}
      >
        {t('opm_pd.finalExport')}
      </Button>
    </>
  );
};

const BatchFileList: FC = () => {
  const t = useTranslation();
  const [selectedBatchIds, setSelectedBatchIds] = useState<string[]>([]);

  const { filterState, adminReportBatchFiles, isLoading, setFilterState, refreshData, onSort } =
    useAdminReportBatchFilePruTable({
      initialParams: initialFilters,
    });

  const selectableBatchIds = useMemo(() => {
    return adminReportBatchFiles?.items.map((batchFile) => String(batchFile.id)) || [];
  }, [adminReportBatchFiles]);

  const selectedCountOnPage = selectableBatchIds.filter((id) => selectedBatchIds.includes(id)).length;
  const isSelectedBatchIdsLimitReached = selectedCountOnPage >= SELECTED_BATCH_IDS_LIMIT;
  const isAllSelectedOnPage =
    selectableBatchIds.length > 0 &&
    (selectedCountOnPage === selectableBatchIds.length || isSelectedBatchIdsLimitReached);
  const isIndeterminateOnPage = selectedCountOnPage > 0 && !isAllSelectedOnPage;

  const handleSelectAllOnPage = (checked: boolean) => {
    setSelectedBatchIds((prev) => {
      if (checked) {
        const remainingSlots = SELECTED_BATCH_IDS_LIMIT - prev.length;
        const batchIdsToSelect = selectableBatchIds.filter((id) => !prev.includes(id)).slice(0, remainingSlots);

        return [...prev, ...batchIdsToSelect];
      }

      const batchIdSet = new Set(selectableBatchIds);
      return prev.filter((id) => !batchIdSet.has(id));
    });
  };

  const handleToggleBatch = (batchId: string) => {
    setSelectedBatchIds((prev) => {
      if (prev.includes(batchId)) {
        return prev.filter((id) => id !== batchId);
      }

      return [...prev, batchId];
    });
  };

  const batchFileIds = useMemo(() => {
    return adminReportBatchFiles?.items?.map((batchFile) => String(batchFile.id)) || [];
  }, [adminReportBatchFiles?.items]);
  const batchFileIdsRef = useRef(batchFileIds);

  useEffect(() => {
    if (_isEqual(batchFileIdsRef.current, batchFileIds)) {
      return;
    }
    setSelectedBatchIds([]);
    batchFileIdsRef.current = batchFileIds;
  }, [batchFileIds]);

  return (
    <>
      <PruFilter
        title={t('opm_pd.filter')}
        itemDef={[
          {
            type: PruFilterItemType.FREE_TEXT,
            field: 'batchNo',
            displayName: t('opm_pd.batchNo'),
          },
          {
            type: PruFilterItemType.DATE_RANGE,
            fieldFrom: 'submittedAtFrom',
            fieldTo: 'submittedAtTo',
            displayName: t('opm_pd.submittedAt'),
          },
          {
            type: PruFilterItemType.FREE_TEXT,
            field: 'submittedBy',
            displayName: t('opm_pd.submittedBy'),
          },
        ]}
        onChangeFilter={(data) => {
          setFilterState({ ...filterState, ...data, page: 1 });
        }}
        fetchData={refreshData}
      />
      <AppTable
        title={`${t('opm_pd.batchFileList')} ${
          selectedBatchIds.length ? `(${t('opm_pd.batchSelected', { count: selectedBatchIds.length })})` : ''
        }`}
        disableBulkSelect
        operationDef={[]}
        startHeaderActions={
          <>
            <PolicyExport
              batchIds={selectedBatchIds}
              onExportSuccess={() => {
                setSelectedBatchIds([]);
              }}
            />
          </>
        }
        columnDef={[
          {
            isId: true,
            hidden: true,
            keyIndex: 'id',
            displayName: '',
            renderData: () => '',
            sortable: true,
            onSort: (sort) => {
              onSort({ key: '', value: sort[''] });
            },
          },
          {
            isId: false,
            hidden: false,
            keyIndex: 'policyId',
            displayName: (
              <>
                <Checkbox
                  checked={isAllSelectedOnPage}
                  indeterminate={isIndeterminateOnPage}
                  onChange={(event) => handleSelectAllOnPage(event.target.checked)}
                  sx={{
                    color: 'white',
                    '&.Mui-checked': { color: 'white' },
                    '&.MuiCheckbox-indeterminate': { color: 'white' },
                  }}
                />
              </>
            ),
            renderData: (row) => {
              const batchId = String(row.id ?? '');
              const checked = selectedBatchIds.includes(batchId);
              const disabled =
                !selectableBatchIds.includes(batchId) ||
                (!checked && selectedBatchIds.length >= SELECTED_BATCH_IDS_LIMIT);

              return (
                <>
                  <Checkbox checked={checked} onClick={() => handleToggleBatch(batchId)} disabled={disabled} />
                </>
              );
            },
            padding: 'checkbox',
            sortable: false,
          },
          {
            isId: false,
            hidden: false,
            keyIndex: 'batchNo',
            displayName: t('opm_pd.batchNo'),
            renderData: (row) => {
              return (
                <Typography variant="inherit" noWrap>
                  {row.batchNo}
                </Typography>
              );
            },
            sortable: false,
            onSort: (sort) => {
              onSort({ key: '', value: sort[''] });
            },
          },
          {
            isId: false,
            hidden: false,
            keyIndex: 'submittedAt',
            displayName: t('opm_pd.submittedDate'),
            renderData: (row) => (row.submittedAt ? formatDateTime(row.submittedAt, 'DD/MM/YYYY') : ''),
            sortable: false,
          },
          {
            isId: false,
            hidden: false,
            keyIndex: 'submittedName',
            displayName: t('opm_pd.submittedBy'),
            renderData: (row) => row.submittedName,
            sortable: false,
          },
          {
            isId: false,
            hidden: false,
            keyIndex: 'submittedPoliciesCount',
            displayName: t('opm_pd.totalPolicies'),
            renderData: (row) => formatNumber(row.submittedPoliciesCount),
            align: 'right',
            sortable: false,
          },
        ]}
        isLoading={isLoading}
        onRefresh={refreshData}
        dataSource={adminReportBatchFiles?.items || []}
        totalPages={adminReportBatchFiles?.totalPages}
        totalRecords={adminReportBatchFiles?.total}
        onChangePage={(page, rowsPerPage) => {
          setFilterState({ ...filterState, page, limit: rowsPerPage });
          refreshData();
        }}
      />
    </>
  );
};

export default BatchFileList;
